import { cn } from "@stylingcv/utils";
import { forwardRef } from "react";

export type InputProps = {
  hasError?: boolean;
  touchOptimized?: boolean;
  size?: "default" | "sm" | "lg" | "touch";
  leadingIcon?: React.ReactNode;
} & React.InputHTMLAttributes<HTMLInputElement>;

const getInputSizeClasses = (size: "default" | "sm" | "lg" | "touch" = "default") => {
  switch (size) {
    case "sm": {
      return "h-9 px-2 py-1 text-sm";
    }
    case "lg": {
      return "h-14 px-4 py-3 text-base";
    }
    case "touch": {
      return "h-12 min-h-11 px-4 py-3 text-base";
    }
    case "default":
    default: {
      return "h-12 px-3 py-2 text-sm";
    }
  }
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { className, type, hasError = false, touchOptimized = false, size = "default", leadingIcon, ...props },
    ref,
  ) => {
    const inputSize = touchOptimized
      ? ("touch" as const)
      : (size as "default" | "sm" | "lg" | "touch");

    if (leadingIcon) {
      return (
        <div
          className={cn(
            "group flex items-center rounded-lg border bg-white transition-[border-color,box-shadow]",
            "hover:border-gray-300",
            hasError ? "border-destructive" : "border-gray-200",
            "focus-within:border-[#268599] focus-within:shadow-[0_0_0_3px_rgba(38,133,153,0.12)]",
          )}
        >
          <span className="flex shrink-0 items-center pl-3 text-gray-400 transition-colors group-focus-within:text-[#268599]">
            {leadingIcon}
          </span>
          <input
            ref={ref}
            type={type}
            autoComplete="off"
            className={cn(
              "min-w-0 flex-1 border-0 bg-transparent px-3 py-[13px] text-[14px] leading-snug outline-none placeholder:text-gray-400 disabled:cursor-not-allowed disabled:opacity-50",
              touchOptimized && "touch-manipulation",
              className,
            )}
            {...props}
          />
        </div>
      );
    }

    return (
      <input
        ref={ref}
        type={type}
        autoComplete="off"
        className={cn(
          "flex w-full rounded-lg border bg-white transition-[border-color,box-shadow] focus-visible:border-[#268599] focus-visible:shadow-[0_0_0_3px_rgba(38,133,153,0.12)] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
          getInputSizeClasses(inputSize),
          hasError ? "border-destructive" : "border-gray-200",
          touchOptimized && "touch-manipulation",
          className,
        )}
        {...props}
      />
    );
  },
);

Input.displayName = "Input";
